import React, {
  useRef,
  useEffect,
  type TextareaHTMLAttributes,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { countWords } from '../../utils/general';
import { textareaColorStyles, textareaSizeClasses } from '../../styles/style';

type TextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size' | 'prefix' | 'suffix'
> & {
  size?: 'xl' | 'lg' | 'md';
  variant?: 'default' | 'outline';
  color?: 'default' | 'success' | 'warning' | 'error' | 'info';
  wrapperClassName?: string;
  label?: string;
  labelClassName?: string;
  helperText?: React.ReactNode;
  helperTextClassName?: string;
  textareaClassName?: string;
  textareaWrapperClassName?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  prefixClassName?: string;
  suffixClassName?: string;
  autogrow?: boolean;
  minRows?: number;
  maxRows?: number;
  autogrowClassName?: string;
  maxWords?: number;
  showWordCount?: boolean;
};

const Textarea = forwardRef((props: TextareaProps, ref) => {
  const {
    size = 'lg',
    variant = 'default',
    color = 'default',
    label,
    labelClassName = '',
    helperText,
    helperTextClassName = '',
    className = '',
    wrapperClassName = '',
    textareaClassName = '',
    textareaWrapperClassName = '',
    prefix,
    suffix,
    prefixClassName = '',
    suffixClassName = '',
    disabled = false,
    autogrow = false,
    minRows = 1,
    maxRows = 10,
    autogrowClassName = '',
    maxWords = 0,
    showWordCount = true,
    ...rest
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [wordCount, setWordCount] = useState(0);

  const adjustHeight = () => {
    if (!autogrow || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight, 10) || 20;
    const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom, 10) || 0;
    const borderTop = parseInt(computedStyle.borderTopWidth, 10) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth, 10) || 0;

    textarea.style.height = 'auto';

    const scrollHeight = textarea.scrollHeight;
    const minHeight =
      lineHeight * minRows +
      paddingTop +
      paddingBottom +
      borderTop +
      borderBottom;
    const maxHeight =
      lineHeight * maxRows +
      paddingTop +
      paddingBottom +
      borderTop +
      borderBottom;

    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight - 20}px`;
  };

  const resetCounter = () => {
    setWordCount(0);
  };

  useImperativeHandle(ref, () => ({
    adjustHeight,
    resetCounter,
  }));

  useEffect(() => {
    if (autogrow) {
      adjustHeight();

      const handleResize = () => adjustHeight();
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [autogrow, minRows, maxRows]);

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    if (maxWords <= wordCount) {
      return;
    }

    if (autogrow) {
      adjustHeight();
    }

    if (maxWords) {
      setWordCount(countWords(e.currentTarget.value));
    }

    if (rest.onInput) {
      rest.onInput(e);
    }
  };

  const disabledClasses = 'cursor-not-allowed bg-gray-50 border-gray-200';
  const baseWrapperClassName =
    'cursor-default duration-300 flex items-center justify-center gap-2';
  const baseTextareaClassName =
    'bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent border-none outline-none flex-grow h-full w-full';
  const baseLabelClassName = 'text-sm' + ' ' + labelClassName;
  const baseHelperTextClassName =
    'text-sm text-gray-400 mb-0' + ' ' + helperTextClassName;
  const basePrefixClassName = `[&>svg]:w-6 [&>svg]:h-6 text-gray-400 flex items-center justify-center ${prefixClassName}`;
  const baseSuffixClassName = `[&>svg]:w-6 [&>svg]:h-6 text-gray-400 flex items-center justify-center ${suffixClassName}`;
  const computedTextareaClassName = `${baseTextareaClassName} ${textareaClassName} ${autogrow ? 'autogrow' : ''} ${disabled ? 'pointer-events-none' : ''}`;
  const computedClassName = `${baseWrapperClassName} ${textareaColorStyles[variant][color]} ${textareaSizeClasses({ autogrow })[size]} ${className} ${disabled ? disabledClasses : ''} ${autogrow ? autogrowClassName : ''} ${textareaWrapperClassName}`;

  return (
    <div className={`flex flex-col gap-2 ${wrapperClassName}`}>
      {label && (
        <label htmlFor={rest.id} className={baseLabelClassName}>
          {label}
        </label>
      )}
      <div className={computedClassName}>
        {prefix && <span className={basePrefixClassName}>{prefix}</span>}
        <div className='relative flex-grow flex'>
          <textarea
            {...rest}
            ref={textareaRef}
            onInput={handleInput}
            className={computedTextareaClassName}
          />
          {showWordCount && maxWords && wordCount > 0 ? (
            <span className='absolute sm:bottom-1 bottom-0 right-0 text-sm !text-gray-500'>
              Wordcount: <span className='!text-green-400'>{wordCount}</span>
            </span>
          ) : (
            <></>
          )}
        </div>
        {suffix && <span className={baseSuffixClassName}>{suffix}</span>}
      </div>
      {helperText && (
        <div className={baseHelperTextClassName}>{helperText}</div>
      )}
    </div>
  );
});

export default Textarea;
